import '@bjb/bjb-web-components/dist/assets/scripts/FallbackTracking.js';

import { getCookie, setCookie } from '../modules/cookie';

type TrackingEvent = Record<string, unknown>;

const TRACKING_TOKEN_COOKIE = 'tracking_token';
const ONE_HOUR_IN_DAYS = 1 / 24;

function hasTrackingConsent(): boolean {
  return Boolean(getCookie('allow_tracking_cookies')) || Boolean(getCookie('allow_marketing_cookies'));
}

function pushTrackingEvents(trackingEvents: TrackingEvent[]): void {
  if (!Array.isArray(trackingEvents) || trackingEvents.length === 0) {
    return;
  }

  if (!Array.isArray(window.adobeDataLayer)) {
    window.adobeDataLayer = [];
  }

  window.adobeDataLayer.push(...trackingEvents);
}

function generateToken(): string {
  if (window.crypto?.getRandomValues) {
    const array = new Uint8Array(16);
    window.crypto.getRandomValues(array);
    return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
  }
  return Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
}

function ensureTrackingToken(): void {
  if (getCookie(TRACKING_TOKEN_COOKIE)) {
    return;
  }

  const token = generateToken();
  setCookie(TRACKING_TOKEN_COOKIE, token, ONE_HOUR_IN_DAYS);
}

function fetchTrackingEvents(eventsEndpoint?: string): void {
  const token = getCookie(TRACKING_TOKEN_COOKIE);
  if (!eventsEndpoint || !token) {
    return;
  }

  fetch(eventsEndpoint, { credentials: 'include' })
    .then((response) => (response.ok ? response.json() : null))
    .then((data) => {
      if (!data || !Array.isArray(data.trackingEvents)) {
        return;
      }
      pushTrackingEvents(data.trackingEvents);
    })
    .catch(() => {});
}

function isFormOpenTrackingEvent(event: Event): boolean {
  const detail = (event as CustomEvent).detail;
  return detail?.form_step_name === 'form open';
}

function hasFormOpenEventInDataLayer(): boolean {
  return Array.isArray(window.adobeDataLayer)
    && window.adobeDataLayer.some((item: any) =>
      item?.event === 'ce_form' && item?.eventInfo?.form_step_name === 'form open'
    );
}

// handle consent update
document.addEventListener('jb-cookie-settings:update', () => {
  if (!hasTrackingConsent()) {
    return;
  }

  if (hasFormOpenEventInDataLayer()) {
    ensureTrackingToken();
  }
});

// handle (potentially) form open events
document.body.addEventListener('jb-tracking', (event) => {
  if (!hasTrackingConsent()) {
    return;
  }

  if (isFormOpenTrackingEvent(event)) {
    ensureTrackingToken();
  }
});

function onLoad() {
  if (!hasTrackingConsent()) {
    return;
  }

  fetchTrackingEvents(document.body.dataset.trackingEventsEndpoint);
}

if (document.readyState === 'complete') {
  onLoad();
} else {
  window.addEventListener('load', onLoad, { once: true });
}
